import React, { useEffect, useState } from 'react';
import { Camera, Paperclip } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const EMPTY_ATTACHMENT = { kind: 'foto', phase: 'durante', name: '', url: '', description: '' };
const inputClass = 'bg-[#12141a] border-gray-700 text-white';

// Photos from a phone can exceed the 10 MB limit; shrink them before uploading.
async function shrinkImage(file, maxSide = 2000, quality = 0.85) {
  if (!file.type.startsWith('image/') || file.size < 1024 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'foto'}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

export function VisitAttachmentDialog({ open, onOpenChange, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY_ATTACHMENT);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(EMPTY_ATTACHMENT);
      setFile(null);
      setError('');
    }
  }, [open]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    const prepared = await shrinkImage(picked);
    if (prepared.size > MAX_ATTACHMENT_BYTES) {
      setError('El archivo pesa más de 10 MB.');
      return;
    }
    setError('');
    setFile(prepared);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!file && !form.url.trim()) {
      setError('Adjunta un archivo o pega un enlace.');
      return;
    }
    if (!file && !form.name.trim()) {
      setError('Ponle un nombre al enlace.');
      return;
    }
    const data = new FormData();
    if (file) data.append('file', file);
    ['name', 'url', 'kind', 'phase', 'description'].forEach((field) => {
      if (form[field]?.trim()) data.append(field, form[field].trim());
    });
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#E2B05E]">Agregar Adjunto</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
              <span className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-700 text-sm text-gray-200">
                <Camera size={18} /> Tomar foto
              </span>
            </label>
            <label className="block">
              <input
                type="file"
                accept="image/*,application/pdf,text/plain,.doc,.docx,.xls,.xlsx"
                className="hidden"
                onChange={handleFile}
              />
              <span className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-700 text-sm text-gray-200">
                <Paperclip size={18} /> Adjuntar archivo
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-400">
            {file ? `Archivo: ${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)` : 'Foto, PDF, Word o Excel, hasta 10 MB. O usa un enlace abajo.'}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => update('kind', v)}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1d23] border-gray-700">
                  <SelectItem value="foto">Foto</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="video">Video (enlace)</SelectItem>
                  <SelectItem value="documento">Documento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Fase</Label>
              <Select value={form.phase} onValueChange={(v) => update('phase', v)}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1d23] border-gray-700">
                  <SelectItem value="antes">Antes</SelectItem>
                  <SelectItem value="durante">Durante</SelectItem>
                  <SelectItem value="despues">Después</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-gray-300">Nombre</Label>
            <Input value={form.name} onChange={(e) => update('name', e.target.value)} className={inputClass} placeholder={file ? file.name : ''} />
          </div>
          <div>
            <Label className="text-gray-300">Enlace (opcional)</Label>
            <Input value={form.url} onChange={(e) => update('url', e.target.value)} className={inputClass} placeholder="https://..." />
          </div>
          <div>
            <Label className="text-gray-300">Descripción</Label>
            <Textarea value={form.description} onChange={(e) => update('description', e.target.value)} className={inputClass} rows={2} />
          </div>
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
            <Button type="submit" disabled={submitting} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
              {submitting ? 'Subiendo…' : 'Agregar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
